'use client';

import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Maximize2, Minimize2, Info, MoreVertical } from 'lucide-react';
import { ChatThreadList } from './ChatThreadList';
import { ChatMessageView } from './ChatMessageView';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { useChatThreads } from '../hooks/useChatThreads';
import type {
  CorrespondenceChatProps,
  ThreadFilters,
  ChatThread,
  ChatMessage as ChatMessageType
} from '../types';

// Mock data import - will be replaced with API data
import { mockCorrespondenceScenarios } from '../mocks/correspondence-data';

export function ChatContainer({
  matterId,
  institutionId,
  institutionName,
  matterReference,
  deceasedName,
  fullHeight = false,
  showThreadList = false
}: CorrespondenceChatProps) {
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [filters, setFilters] = useState<ThreadFilters>({});
  const [view, setView] = useState<'threads' | 'messages'>('threads');

  // Get mock data based on matterId (will be replaced with API call)
  const mockCorrespondence =
    matterId === 99
      ? mockCorrespondenceScenarios.empty
      : mockCorrespondenceScenarios.active.filter((c) =>
          institutionId ? c.institutionId === institutionId : true
        );

  // Transform correspondence into unified thread or multiple threads based on view mode
  const { threads, unifiedThread } = useChatThreads(
    mockCorrespondence,
    institutionName,
    filters,
    !showThreadList // Use unified view when not showing thread list
  );

  // Use unified thread or select from multiple threads
  const selectedThread = showThreadList
    ? threads.find((t) => t.id === selectedThreadId) || threads[0] || null
    : unifiedThread;

  const handleThreadSelect = (threadId: string) => {
    setSelectedThreadId(threadId);
    // On mobile, switch to message view when selecting a thread
    if (window.innerWidth < 768) {
      setView('messages');
    }
  };

  const handleSendMessage = (content: string) => {
    // TODO: Implement with API
    console.log('Sending message:', content, 'to matter:', matterId);
  };

  const handleAttachment = () => {
    // TODO: Implement file upload
    console.log('Opening file attachment dialog');
  };

  const renderChat = (extraClassName?: string) => (
    <div
      className={cn(
        'bg-card flex rounded-lg',
        fullHeight ? 'h-full' : 'h-[600px]',
        'relative',
        extraClassName
      )}
    >
      {/* Thread List - Only show when showThreadList is true */}
      {showThreadList && (
        <div
          className={cn(
            'w-full border-r md:w-96',
            view === 'messages' && 'hidden md:block'
          )}
        >
          <ChatThreadList
            threads={threads}
            selectedThreadId={selectedThread?.id}
            onThreadSelect={handleThreadSelect}
            filters={filters}
            onFiltersChange={setFilters}
          />
        </div>
      )}

      {/* Message View - Full width when no thread list */}
      {selectedThread && fullHeight && !showThreadList && (
        <>
          {/* Header */}
          <div className="border-border bg-card shrink-0 border-b px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
                  <span className="text-primary text-sm font-semibold">
                    {institutionName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <h2 className="text-base font-semibold">{institutionName}</h2>
                  {selectedThread.unreadCount > 0 && (
                    <p className="text-muted-foreground text-xs">
                      {selectedThread.unreadCount} new message
                      {selectedThread.unreadCount === 1 ? '' : 's'}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon-sm">
                  <Info className="h-4 w-4" />
                  <span className="sr-only">Information</span>
                </Button>
                <Button variant="ghost" size="icon-sm">
                  <MoreVertical className="h-4 w-4" />
                  <span className="sr-only">More options</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <MessageScrollArea thread={selectedThread} />

          {/* Sticky Input */}
          <div className="border-border bg-card border-t">
            <ChatInput
              onSend={handleSendMessage}
              onAttach={handleAttachment}
              placeholder={`Message ${institutionName}...`}
            />
          </div>
        </>
      )}

      {/* Non-fullHeight or with thread list */}
      {selectedThread && (!fullHeight || showThreadList) && (
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col overflow-hidden',
            showThreadList && view === 'threads' && 'hidden md:block'
          )}
        >
          <ChatMessageView
            thread={selectedThread}
            onSendMessage={handleSendMessage}
            onAttachment={handleAttachment}
            institutionName={institutionName}
            matterReference={matterReference}
            deceasedName={deceasedName}
            fullHeight={false}
          />
        </div>
      )}

      {/* Mobile view toggle - Only show on mobile */}
      {view === 'messages' && (
        <Button
          variant="ghost"
          size="sm"
          className="absolute top-2 left-2 md:hidden"
          onClick={() => setView('threads')}
        >
          ← Back
        </Button>
      )}
    </div>
  );

  if (isFullScreen) {
    return (
      <>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsFullScreen(true)}
          className="absolute -top-10 right-0"
        >
          <Maximize2 className="h-4 w-4" />
          <span className="sr-only">Fullscreen</span>
        </Button>

        <Dialog open={isFullScreen} onOpenChange={setIsFullScreen}>
          <DialogContent className="h-[90vh] w-full max-w-[95vw] p-0">
            <DialogHeader className="border-b px-6 py-4">
              <div className="flex items-center justify-between">
                <DialogTitle>Correspondence with {institutionName}</DialogTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsFullScreen(false)}
                >
                  <Minimize2 className="h-4 w-4" />
                  <span className="sr-only">Exit fullscreen</span>
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-hidden">
              {renderChat('rounded-none border-0')}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsFullScreen(true)}
        className="absolute -top-10 right-0 z-10"
      >
        <Maximize2 className="h-4 w-4" />
        <span className="sr-only">Fullscreen</span>
      </Button>
      {renderChat()}
    </div>
  );
}

// Simple message scroll area for full height mode
interface MessageScrollAreaProps {
  thread: ChatThread;
}

function MessageScrollArea({ thread }: MessageScrollAreaProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]'
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [thread.messages]);

  // Group consecutive messages from same sender
  const groupedMessages = groupMessages(thread.messages);

  return (
    <ScrollArea className="bg-card flex-1" ref={scrollAreaRef}>
      <div className="mx-auto max-w-4xl px-4 py-4">
        {groupedMessages.map((group, groupIndex) => (
          <div key={group[0].id}>
            {/* Date separator */}
            {shouldShowDateSeparator(
              group[0].timestamp,
              groupIndex > 0
                ? groupedMessages[groupIndex - 1][0].timestamp
                : null
            ) && <DateSeparator date={group[0].timestamp} />}

            {/* Messages in group */}
            {group.map((message, messageIndex) => (
              <ChatMessage
                key={message.id}
                message={message}
                showTimestamp={messageIndex === group.length - 1}
                isGrouped={messageIndex > 0}
              />
            ))}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

// Helper functions
function groupMessages(messages: ChatMessageType[]): ChatMessageType[][] {
  const groups: ChatMessageType[][] = [];
  let currentGroup: ChatMessageType[] = [];
  let lastSender: string | null = null;

  messages.forEach((message) => {
    if (message.sender !== lastSender && currentGroup.length > 0) {
      groups.push(currentGroup);
      currentGroup = [];
    }
    currentGroup.push(message);
    lastSender = message.sender;
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

function shouldShowDateSeparator(
  current: Date,
  previous: Date | null
): boolean {
  if (!previous) return true;

  const currentDate = current.toDateString();
  const previousDate = previous.toDateString();

  return currentDate !== previousDate;
}

function DateSeparator({ date }: { date: Date }) {
  const formatDate = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  return (
    <div className="my-6 flex items-center justify-center">
      <div className="bg-muted rounded-full px-3 py-1">
        <span className="text-foreground-secondary text-xs font-medium">
          {formatDate(date)}
        </span>
      </div>
    </div>
  );
}
